import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Calendar } from '../ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Search, CalendarIcon, X, Filter, UserCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { SingleDayFilter, QuickRangeButtons } from '../dashboard/FilterComponents';

export const InvoicesFilters = ({
    searchTerm,
    setSearchTerm,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    selectedUserId,
    setSelectedUserId,
    users,
    canFilterByUser,
    hasActiveFilters,
    onSearch,
    onClear,
    onSelectDay,
    onSelectRange
}) => {
    return (
        <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                    {/* Search Input */}
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Buscar por nombre, descripción o usuario..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                            className="pl-9 h-10"
                            data-testid="search-input"
                        />
                    </div>

                    {/* Date From */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={`w-[160px] justify-start text-left font-normal ${!dateFrom && 'text-slate-400'}`}
                                data-testid="date-from-btn"
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Desde'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={dateFrom}
                                onSelect={setDateFrom}
                                locale={es}
                                defaultMonth={new Date()}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>

                    {/* Date To */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={`w-[160px] justify-start text-left font-normal ${!dateTo && 'text-slate-400'}`}
                                data-testid="date-to-btn"
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Hasta'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={dateTo}
                                onSelect={setDateTo}
                                locale={es}
                                defaultMonth={new Date()}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>

                    {/* Single day (picks month + day, filters immediately) */}
                    <SingleDayFilter
                        day={dateFrom && dateTo && dateFrom.getTime() === dateTo.getTime() ? dateFrom : null}
                        onSelectDay={onSelectDay}
                        testId="invoices-single-day-btn"
                    />
                    <QuickRangeButtons
                        onSelectRange={onSelectRange}
                        activeFrom={dateFrom}
                        activeTo={dateTo}
                        testIdPrefix="invoices"
                    />

                    {/* User Filter - Only for admin/supervisor */}
                    {canFilterByUser && (
                        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                            <SelectTrigger className="w-[180px]" data-testid="invoices-user-filter">
                                <UserCircle className="mr-2 h-4 w-4 text-slate-400" />
                                <SelectValue placeholder="Usuario" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                        {u.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    {/* Filter Actions */}
                    <Button
                        onClick={onSearch}
                        className="bg-sky-500 hover:bg-sky-600"
                        data-testid="apply-filters-btn"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filtrar
                    </Button>

                    {hasActiveFilters && (
                        <Button
                            variant="ghost"
                            onClick={onClear}
                            className="text-slate-500 hover:text-slate-700"
                            data-testid="clear-filters-btn"
                        >
                            <X className="w-4 h-4 mr-1" />
                            Limpiar
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default InvoicesFilters;
